// Today: per-time-slot view of the user's stacks. Mirrors the APK's
// HomeShell → Today tab. Slots are morning / midday / evening / bedtime —
// each is one UserStack identified by `slot_id`. The card for the current
// hour-of-day is highlighted; checking an item persists via tracking_store
// so the APK and web share the same daily check state through Supabase.

import { loadStacks, loadStacksAsync, type UserStack } from '../lib/stack_store';
import { COMPOUND_BY_ID } from '../lib/compounds';
import { toggleItem, isItemChecked } from '../lib/tracking_store';

type Slot = { id: string; label: string; emoji: string; startHour: number; endHour: number };

const SLOTS: Slot[] = [
  { id: 'morning', label: 'Morning Stack', emoji: '🌅', startHour: 5,  endHour: 11 },
  { id: 'midday',  label: 'Midday Stack',  emoji: '☀️', startHour: 11, endHour: 16 },
  { id: 'evening', label: 'Evening Stack', emoji: '🌇', startHour: 16, endHour: 21 },
  { id: 'bedtime', label: 'Bedtime Stack', emoji: '🌙', startHour: 21, endHour: 29 },
];

function currentSlotIndex(now: Date): number {
  const h = now.getHours();
  for (let i = 0; i < SLOTS.length; i++) {
    const s = SLOTS[i];
    const wraps = s.endHour > 24;
    const endH = wraps ? s.endHour - 24 : s.endHour;
    const inSlot = wraps ? (h >= s.startHour || h < endH) : (h >= s.startHour && h < s.endHour);
    if (inSlot) return i;
  }
  return 0;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function renderSlotCard(slot: Slot, stack: UserStack | null, isCurrent: boolean): string {
  const items = stack?.items?.filter((i) => i.active) ?? [];
  const ring = isCurrent ? 'border:2px solid var(--acc);' : 'border:1px solid var(--line);';
  const checkedCount = items.filter((i) => isItemChecked(stack!.id, i.id)).length;
  const progress = items.length ? Math.round((checkedCount / items.length) * 100) : 0;

  let body = '';
  if (!stack || !items.length) {
    body = `<p style="color:var(--dim);margin:8px 0 0;font-size:13px;">No items in this slot yet. Add compounds to the <a href="/stacks" style="color:var(--acc);">${esc(slot.label)}</a>.</p>`;
  } else {
    body = items.map((it) => {
      const c = COMPOUND_BY_ID[it.compound_id];
      const name = c?.display_name ?? it.compound_id;
      const dose = it.dose_value != null
        ? `${it.dose_value} ${esc(it.unit || '')}`
        : (it.dose_mg != null ? `${it.dose_mg} mg` : '');
      const checked = isItemChecked(stack.id, it.id);
      return `
        <label class="today-row" data-stack="${esc(stack.id)}" data-item="${esc(it.id)}"
               style="display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:6px;cursor:pointer;background:${checked ? 'rgba(95,196,128,0.10)' : 'transparent'};">
          <input type="checkbox" ${checked ? 'checked' : ''} style="width:18px;height:18px;accent-color:var(--acc);" />
          <span style="flex:1;color:${checked ? 'var(--dim)' : 'var(--ink)'};${checked ? 'text-decoration:line-through;' : ''}">${esc(name)}</span>
          <span style="color:var(--dim);font-size:12px;">${esc(dose)}</span>
        </label>`;
    }).join('');
  }

  return `
    <section class="today-card" style="background:var(--bg-2);${ring}border-radius:10px;padding:14px 16px;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:6px;">
        <span style="font-size:22px;">${slot.emoji}</span>
        <h2 style="margin:0;font-size:17px;flex:1;">${esc(slot.label)}</h2>
        ${stack && items.length ? `<span style="color:var(--dim);font-size:12px;">${checkedCount}/${items.length} · ${progress}%</span>` : ''}
        ${isCurrent ? `<span style="background:var(--acc);color:#062014;font-size:10px;font-weight:700;padding:2px 7px;border-radius:10px;">NOW</span>` : ''}
      </div>
      ${body}
    </section>`;
}

export async function renderToday(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;

  // Read local immediately so first paint is instant; refresh from cloud after.
  let stacks: UserStack[] = loadStacks();
  const now = new Date();
  const curIdx = currentSlotIndex(now);

  const paint = () => {
    const cards = SLOTS.map((slot, i) => {
      const stack = stacks.find((s) => s.slot_id === slot.id) ?? null;
      return renderSlotCard(slot, stack, i === curIdx);
    }).join('');
    app.innerHTML = `
      <h1 style="margin:6px 0 16px;font-size:24px;">Today</h1>
      <p style="color:var(--dim);margin:0 0 14px;font-size:13px;">Your day at a glance. Tap items as you take them.</p>
      <div style="display:flex;flex-direction:column;gap:12px;">${cards}</div>
    `;

    // Wire check rows
    app.querySelectorAll<HTMLElement>('.today-row').forEach((row) => {
      row.addEventListener('click', (e) => {
        // Avoid double-toggle when clicking the checkbox directly
        if ((e.target as HTMLElement).tagName === 'INPUT') return;
        e.preventDefault();
        const stackId = row.dataset.stack!;
        const itemId = row.dataset.item!;
        toggleItem(stackId, itemId);
        paint();
      });
      row.querySelector<HTMLInputElement>('input[type=checkbox]')?.addEventListener('change', () => {
        const stackId = row.dataset.stack!;
        const itemId = row.dataset.item!;
        toggleItem(stackId, itemId);
        paint();
      });
    });
  };

  paint();

  try {
    const fresh = await loadStacksAsync();
    if (fresh && fresh.length) {
      stacks = fresh;
      paint();
    }
  } catch {/* offline — local is good enough */}
}
